import { inWakeWindow, vnClockMinute, type WakeGroup } from '@mt/shared';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { useAnnounceSettings } from './announce';

/**
 * Giữ màn hình sáng để nghe đọc việc giao (DEC-671, anh Tiến 02/11/2026):
 *  - Nhân viên: 07:00–07:30, 10:00–11:00, 16:50–17:15; tài xế thêm 21:00, 22:00 và cả lúc cắm sạc; công trình không giữ.
 *  - Dùng Screen Wake Lock (Chrome Android, Safari iPhone 16.4+): chỉ giữ khi ERP đang mở trên màn hình;
 *    người dùng bấm nút nguồn tắt màn thì vẫn tắt (hệ điều hành nhả khóa), mở lại ERP thì giữ tiếp.
 *  - iPhone không cho web biết đang sạc → tài xế bật tay "Giữ sáng khi cắm sạc" (công tắc trên máy).
 */
interface BatteryLike extends EventTarget {
  charging: boolean;
}
type WakeLockSentinelLike = { released: boolean; release: () => Promise<void>; addEventListener: (t: 'release', cb: () => void) => void };

export const wakeLockSupported = () => typeof navigator !== 'undefined' && 'wakeLock' in navigator;
export const batterySupported = () => typeof navigator !== 'undefined' && 'getBattery' in navigator;

/** Đang sạc: Android/Chrome đọc được; máy không cho đọc → null. */
export function useCharging(): boolean | null {
  const [charging, setCharging] = useState<boolean | null>(null);
  useEffect(() => {
    if (!batterySupported()) return;
    let bat: BatteryLike | null = null;
    let alive = true;
    const on = () => bat && setCharging(bat.charging);
    void (navigator as unknown as { getBattery: () => Promise<BatteryLike> })
      .getBattery()
      .then((b) => {
        if (!alive) return;
        bat = b;
        on();
        b.addEventListener('chargingchange', on);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      bat?.removeEventListener('chargingchange', on);
    };
  }, []);
  return charging;
}

/** Bây giờ có nên giữ sáng không. charging = null (không đọc được) → coi như cắm sạc nếu tài xế bật công tắc. */
export function shouldWake(group: WakeGroup | null, now: Date, opt: { wake: boolean; wakeCharging: boolean; charging: boolean | null }): boolean {
  if (!group || !opt.wake) return false;
  if (group === 'driver' && opt.wakeCharging && opt.charging !== false) return true;
  return inWakeWindow(group, vnClockMinute(now));
}

// Trạng thái hiện tại cho thẻ cài đặt (nhóm của người đăng nhập, đang giữ sáng không)
let state: { group: WakeGroup | null; on: boolean } = { group: null, on: false };
const subs = new Set<() => void>();
const setState = (next: typeof state) => {
  if (next.group === state.group && next.on === state.on) return;
  state = next;
  subs.forEach((f) => f());
};
export const useWakeState = () =>
  useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => state,
    () => state,
  );

export function useScreenWake(group: WakeGroup | null) {
  const st = useAnnounceSettings();
  const charging = useCharging();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const want = shouldWake(group, new Date(), { wake: st.wake, wakeCharging: st.wakeCharging, charging: batterySupported() ? charging : null }) && tick >= 0;

  useEffect(() => setState({ group, on: want }), [group, want]);

  useEffect(() => {
    if (!want || !wakeLockSupported()) return;
    let lock: WakeLockSentinelLike | null = null;
    let disposed = false;
    const acquire = async () => {
      if (disposed || document.visibilityState !== 'visible' || (lock && !lock.released)) return;
      try {
        lock = (await (navigator as unknown as { wakeLock: { request: (t: 'screen') => Promise<WakeLockSentinelLike> } }).wakeLock.request('screen')) ?? null;
      } catch {
        lock = null; // pin yếu / chế độ tiết kiệm pin: trình duyệt từ chối, bỏ qua
      }
    };
    const onVis = () => void acquire();
    void acquire();
    document.addEventListener('visibilitychange', onVis);
    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVis);
      void lock?.release().catch(() => undefined);
    };
  }, [want]);
  return want;
}
